/** Кастомный селектор локации.
 *
 *  Триггер (внутри chrome-хедера) стилизован под chrome-токены:
 *    chrome-2 background, chrome-border, chrome-ink text.
 *  Выпадающий popover — контент-overlay, стилизован под surface (светлый
 *  даже в теме calm, потому что это всплывающий контент над страницей).
 */
import { type Component, For, Show, createMemo, createSignal, onCleanup, onMount } from "solid-js";
import { ChevronDown } from "lucide-solid";
import type { Location } from "~/types";
import { useI18n } from "~/i18n";

interface Props {
  active: Location | null;
  available: Location[];
  onSelect: (id: number | null) => void;
}

interface CompanyGroup {
  company_id: number;
  company_name: string;
  locations: Location[];
}

export const LocationDropdown: Component<Props> = (props) => {
  const [open, setOpen] = createSignal(false);
  const { t } = useI18n();
  let rootRef: HTMLDivElement | undefined;

  const onDocClick = (e: MouseEvent) => {
    if (rootRef && !rootRef.contains(e.target as Node)) setOpen(false);
  };

  onMount(() => {
    document.addEventListener("mousedown", onDocClick);
    onCleanup(() => document.removeEventListener("mousedown", onDocClick));
  });

  // Группируем локации по компании-владельцу для удобства поиска в дропдауне.
  const groups = createMemo<CompanyGroup[]>(() => {
    const byId = new Map<number, CompanyGroup>();
    for (const loc of props.available) {
      const g = byId.get(loc.company_id);
      if (g) {
        g.locations.push(loc);
      } else {
        byId.set(loc.company_id, {
          company_id: loc.company_id,
          company_name: loc.company_name || "—",
          locations: [loc],
        });
      }
    }
    return Array.from(byId.values());
  });

  return (
    <div ref={rootRef} class="relative">
      <button
        type="button"
        onClick={() => setOpen(!open())}
        class="flex h-9 items-center gap-2 rounded-md border border-chrome-border bg-chrome-2 px-3 text-left text-chrome-ink transition-colors hover:bg-chrome-hover"
      >
        <span class="h-1.5 w-1.5 rounded-full bg-success" />
        <span class="min-w-0 leading-tight">
          <Show
            when={props.active}
            fallback={
              <span class="text-md font-medium">{t("locationDropdown.selectLocation")}</span>
            }
          >
            <span class="block max-w-[220px] truncate text-md font-medium">
              {props.active!.display_name}
            </span>
            <Show when={props.active!.company_name}>
              <span class="block max-w-[220px] truncate text-2xs text-chrome-ink-muted">
                {props.active!.company_name}
              </span>
            </Show>
          </Show>
        </span>
        <ChevronDown
          size={14}
          class="shrink-0 text-chrome-ink-muted transition-transform"
          classList={{ "rotate-180": open() }}
        />
      </button>

      <Show when={open()}>
        <div class="absolute right-0 top-full z-50 mt-1.5 w-[320px] overflow-hidden rounded-lg border border-border bg-surface text-ink shadow-lg">
          <div class="max-h-[60vh] overflow-auto py-1">
            <For
              each={groups()}
              fallback={
                <div class="px-3 py-2 text-md text-ink-muted">{t("locationDropdown.noLocations")}</div>
              }
            >
              {(group) => (
                <div>
                  <div class="border-b border-border-faint bg-surface-2 px-3 py-1.5 text-2xs font-semibold uppercase tracking-wide text-ink-muted">
                    {group.company_name}
                  </div>
                  <For each={group.locations}>
                    {(loc) => {
                      const isActive = () => loc.id === props.active?.id;
                      return (
                        <button
                          type="button"
                          onClick={() => {
                            props.onSelect(loc.id);
                            setOpen(false);
                          }}
                          class="flex w-full items-start gap-2 px-3 py-2 text-left transition-colors hover:bg-surface-hover"
                          classList={{ "bg-primary-soft": isActive() }}
                        >
                          <span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-success" />
                          <span class="min-w-0 flex-1">
                            <span class="block truncate text-md font-medium text-ink">
                              {loc.display_name}
                            </span>
                            <Show when={loc.address}>
                              <span class="block truncate text-sm text-ink-muted">
                                {loc.address}
                              </span>
                            </Show>
                          </span>
                        </button>
                      );
                    }}
                  </For>
                </div>
              )}
            </For>
          </div>
        </div>
      </Show>
    </div>
  );
};
